import React, {useState} from 'react';

import {Dropdown, Row} from 'react-bootstrap';
import {FormModal} from 'components/modals';
import {ModalAlert} from '@/components/alerts';
import {Tooltip} from '@/components/tooltips';
import {Button} from '@/components/ui';
import {Icon} from '@/components/icons/Icon';
import PaginationComponent from '@/components/pagination/PaginationComponent';
import Col from 'react-bootstrap/Col';
import axios from 'axios';
import {useStoreActions} from 'easy-peasy';
import LoadingWrapper from '@/components/loading/LoadingWrapper';   
import NotificationsSettingsForm from './NotificationsSettingsForm';
import NotificationConfigureForm from './NotificationConfigureForm';
import NotificationUserConfigureTable from './NotificationUserConfigureTable';
import useOptimizedConfig from '@/hooks/useOptimizedConfig';
import HelpTextBlock from '@/components/help_text/HelpTextBlock';
import TableComponent from '@/components/table/Table';
import AwesomeDebouncePromise from 'awesome-debounce-promise';
import usePaginatedData from '@/utils/usePaginatedData';
import Form from 'react-bootstrap/Form';
import i18n from 'i18n';
import {hasPermission} from '@/utils/permissions/permissionDebug';

interface UserData {
    permissions?: string[];
}

interface Notification {
    id: number;
    title: string;
    createdBy: {
        userDisplayName: string;
    };
    schedule: string;
    storyboardStoryInfo: string;
    active: boolean;
    usersConfigured: Record<string, any>;
}

interface NotificationsStore {
    notifications: {
        deleteOne: (id: number) => void;
        setReload: (value: boolean) => void;
    };
}

const setSearchQueryState = async (
    setSearchQuery: (value: string) => void,
    value: string,
    setPage: (page: number) => void
): Promise<void> => {
    setPage(1);
    setSearchQuery(value);
};

const setSearchQueryStateDebounced = AwesomeDebouncePromise(setSearchQueryState, 350);

function getConfiguredRecipientsForPreview(usersConfigured: Notification['usersConfigured']): string[] {
    const lines: string[] = [];

    const append = (username: string, email: string): void => {
        const u = username.trim();
        const e = email.trim();
        if (!u && !e) {
            return;
        }
        if (u && e) {
            lines.push(`${u} (${e})`);
        } else {
            lines.push(u || e);
        }
    };

    if (Array.isArray(usersConfigured)) {
        for (const raw of usersConfigured) {
            if (raw && typeof raw === 'object') {
                append(String((raw as { username?: string }).username ?? ''), String((raw as { email?: string }).email ?? ''));
            }
        }
        return lines;
    }

    if (usersConfigured && typeof usersConfigured === 'object') {
        for (const raw of Object.values(usersConfigured)) {
            if (raw && typeof raw === 'object') {
                append(String((raw as { username?: string }).username ?? ''), String((raw as { email?: string }).email ?? ''));
            }
        }
    }

    return lines;
}

const NotificationsListView: React.FC = React.memo(() => {
    const {userData, loading: loadingUserData} = useOptimizedConfig();
    const [searchQuery, setSearchQuery] = useState<string>('');

    const [settingsModalOpen, setSettingsModalOpen] = useState<boolean>(false);
    const [configureModalOpen, setConfigureModalOpen] = useState<boolean>(false);
    const [activeNotificationId, setActiveNotificationId] = useState<number | null>(null);
    const [configureFormKey, setConfigureFormKey] = useState<number>(Math.random());

    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState<boolean>(false);
    const [itemToDelete, setItemToDelete] = useState<{ id: number, title: string } | null>(null);
    const [isDeleting, setIsDeleting] = useState<boolean>(false);

    const [manualSendOpen, setManualSendOpen] = useState<boolean>(false);
    const [manualSendItem, setManualSendItem] = useState<{ id: number; title: string } | null>(null);
    const [manualSendStep, setManualSendStep] = useState<'confirm' | 'done'>('confirm');
    const [manualSendVariant, setManualSendVariant] = useState<'info' | 'success' | 'error'>('info');
    const [manualSendTitle, setManualSendTitle] = useState<string>('');
    const [manualSendBody, setManualSendBody] = useState<string>('');
    const [manualSendLoading, setManualSendLoading] = useState<boolean>(false);

    const {
        data: notifications,
        loading,
        currentPage,
        setCurrentPage,
        pages,
    } = usePaginatedData(window.URLS.notificationsResource, {
        storeResolver: 'notifications',
        requestParams: {
            params: {
                query: searchQuery,
            },
        },
    });

    const userCanAccessNotificationResourceView = hasPermission('fs.notification.read', userData?.permissions, true);
    const canCreateNotification = hasPermission('fs.notification.create', userData?.permissions, true);
    const canChangeNotification = hasPermission('fs.notification.update', userData?.permissions, true);
    const canSendNotificationReport = hasPermission('fs.notification.send', userData?.permissions, true);
    const canDeleteNotification = hasPermission('fs.notification.delete', userData?.permissions, true);

    const userCanAddRemoveRecipients = hasPermission('fs.notification.user_assignment.create', userData?.permissions, true);
    const deleteNotification = useStoreActions<NotificationsStore>((actions) => actions.notifications.deleteOne);
    const setReloadNotifications = useStoreActions<NotificationsStore>((actions) => actions.notifications.setReload);

    const inputChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
        setSearchQueryStateDebounced(setSearchQuery, event.target.value, setCurrentPage);
    };

    const active = currentPage;
    let items: React.ReactNode;

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    if (!userCanAccessNotificationResourceView && !loadingUserData) {
        return (
            <>
                <HelpTextBlock helpText={i18n.t('ht_email_reports.text')} />
                <div className="alert alert-danger" role="alert">
                    {i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')}
                </div>
            </>
        );
    }

    const openSettingsModal = (id: number): void => {
        setActiveNotificationId(id);
        setSettingsModalOpen(true);
    };

    const closeSettingsModal = (): void => {
        setActiveNotificationId(null);
        setSettingsModalOpen(false);
    };

    const openConfigureModal = (id: number): void => {
        setActiveNotificationId(id);
        setConfigureModalOpen(true);
    };

    const closeConfigureModal = (): void => {
        setActiveNotificationId(null);
        setConfigureModalOpen(false);
    };

    const resetConfigureForm = (): void => {
        setConfigureFormKey(Math.random());
    };


    const openDeleteConfirm = (id: number, title: string): void => {
        setItemToDelete({id, title});
        setDeleteConfirmOpen(true);
    };

    const closeDeleteConfirm = (): void => {
        setItemToDelete(null);
        setDeleteConfirmOpen(false);
        setIsDeleting(false);
    };

    const confirmDelete = async (): Promise<void> => {
        if (!itemToDelete) {return;}

        setIsDeleting(true);
        try {
            const url = `${window.URLS.notificationsResource}/${itemToDelete.id}`;
            await axios.delete(url);
            deleteNotification(itemToDelete.id);
            setReloadNotifications(true);
            closeDeleteConfirm();
        } catch (error) {
            setIsDeleting(false);
            throw error;
        }
    };

    const deleteOne = (id: number, title: string): void => {
        openDeleteConfirm(id, title);
    };

    const openManualSendModal = (notification: Notification): void => {
        setManualSendItem({id: notification.id, title: notification.title});
        setManualSendStep('confirm');
        setManualSendVariant('info');
        setManualSendTitle(i18n.t('jt_confirm_send_email_report_title.text'));
        const intro = i18n.t('es_confirm_send_email_report.text', {title: notification.title});
        const recipientLabels = getConfiguredRecipientsForPreview(notification.usersConfigured);
        const recipientsBlock =
            recipientLabels.length > 0
                ? `\n\n${i18n.t('jt_manual_send_configured_recipients_heading.text')}\n${recipientLabels.map((line) => `• ${line}`).join('\n')}`
                : `\n\n${i18n.t('jt_manual_send_no_recipients_configured.text')}`;
        setManualSendBody(intro + recipientsBlock);
        setManualSendLoading(false);
        setManualSendOpen(true);
    };

    const closeManualSendModal = (): void => {
        setManualSendOpen(false);
        setManualSendItem(null);
        setManualSendStep('confirm');
        setManualSendLoading(false);
    };

    const getManualSendErrorMessage = (error: unknown): string => {
        if (typeof error === 'object' && error !== null && 'response' in error) {
            const axiosError = error as { response?: { data?: { error?: string; message?: string } } };
            const data = axiosError.response?.data;
            if (typeof data?.error === 'string') {
                return data.error;
            }
            if (typeof data?.message === 'string') {
                return data.message;
            }
        }
        if (error instanceof Error) {
            return error.message;
        }
        return i18n.t('es_manual_report_send_unknown_error.text');
    };

    const confirmManualSend = async (): Promise<void> => {
        if (!manualSendItem) {
            return;
        }
        setManualSendLoading(true);
        try {
            const url = `${window.URLS.notificationsResource}/${manualSendItem.id}/send`;
            const {data} = await axios.post<{ message?: string; recipients?: string[] }>(url);
            const recipientEmails = Array.isArray(data.recipients) ? data.recipients.filter(Boolean) : [];
            const baseMessage = (data.message ?? '').trim();
            const deliveredBlock =
                recipientEmails.length > 0
                    ? `\n\n${i18n.t('jt_manual_send_recipients_delivered_heading.text')}\n${recipientEmails.map((email) => `• ${email}`).join('\n')}`
                    : '';
            const detail = `${baseMessage}${deliveredBlock}`.trim();
            setManualSendStep('done');
            setManualSendVariant('success');
            setManualSendTitle(i18n.t('jt_manual_report_send_success_title.text'));
            setManualSendBody(detail);
        } catch (error) {
            setManualSendStep('done');
            setManualSendVariant('error');
            setManualSendTitle(i18n.t('jt_manual_report_send_error_title.text'));
            setManualSendBody(getManualSendErrorMessage(error));
        } finally {
            setManualSendLoading(false);
        }
    };

    if (loadingUserData) {
        return <LoadingWrapper loading={true} loadingText={i18n.t('jt_loading_data_general.text')} minHeight="400px"><div /></LoadingWrapper>;
    }

    const columns = [
        {
            accessorKey: 'title',
            header: i18n.t('th_title.text'),
        },
        {
            accessorKey: 'createdBy.userDisplayName',
            header: i18n.t('th_configured.text'),
        },
        {
            accessorKey: 'schedule',
            header: i18n.t('th_schedule.text'),
        },
        {
            accessorKey: 'storyboardStoryInfo',
            header: i18n.t('th_storyboard.text'),
        },
        {
            accessorKey: 'active',
            header: i18n.t('th_active.text'),
            cell: (cell: any) => {
                if (cell.getValue() === true) {
                    return (<Icon name="mdi-check" className="text-success" />);
                } else {
                    return (<Icon name="mdi-close" className="text-danger" />);
                }
            },
        },
        {
            header: i18n.t('th_add_remove.text'),
            cell: (cell: any) => (
                <>
                    {
                        userCanAddRemoveRecipients ? (
                            <Button
                                variant="outline-info"
                                onClick={() => openConfigureModal(cell.row.original.id)}
                            >
                                Add/remove recipients (
                                {Object.keys(cell.row.original.usersConfigured).length}
                                )
                            </Button>
                        ) : (
                            <Tooltip
                                content={i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')}
                                placement="top"
                            >
                                <div
                                    className="ml-auto"
                                    style={{cursor: 'not-allowed', display: 'flex'}}
                                >
                                    <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        disabled
                                        style={{pointerEvents: 'none'}}
                                        data-cy="notifications-list-outlinesecondary-btn">
                                        Add/remove recipients (
                                        {Object.keys(cell.row.original.usersConfigured).length}
                                        )
                                    </Button>
                                </div>
                            </Tooltip>
                        )
                    }
                </>
            ),
        },
        {
            header: i18n.t('th_action.text'),
            cell: (cell: any) => (
                <>
                    <Tooltip content={i18n.t('jt_actions.text')} placement="top">
                        <Dropdown className="d-inline-block">
                            <Dropdown.Toggle
                                as={Button}
                                variant="outline-info"
                                size="sm"
                                icon="mdi-dots-horizontal"
                                className="dropdown-toggle-no-caret"
                                id="settings-dropdown-1"
                                data-cy="notifications-list-outlineinfo-btn-1"
                            />

                            <Dropdown.Menu align="end">
                                {
                                    canSendNotificationReport ? (
                                        <Dropdown.Item
                                            onClick={() => openManualSendModal(cell.row.original)}
                                            data-cy="notifications-list-send-report-btn"
                                        >
                                            <Icon name="mdi-send" className="mr-2" />
                                            {i18n.t('jt_send_email_report_manually.text')}
                                        </Dropdown.Item>
                                    ) : (
                                        <Tooltip
                                            content={i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')}
                                            placement="top"
                                        >
                                            <div
                                                className="ml-auto"
                                                style={{
                                                    cursor: 'not-allowed',
                                                    display: 'flex',
                                                }}
                                            >
                                                <Dropdown.Item
                                                    style={{pointerEvents: 'none'}}
                                                    disabled
                                                    data-cy="notifications-list-send-report-disabled"
                                                >
                                                    <Icon name="mdi-send" className="mr-2" />
                                                    {i18n.t('jt_send_email_report_manually.text')}
                                                </Dropdown.Item>
                                            </div>
                                        </Tooltip>
                                    )
                                }

                                {
                                    canChangeNotification ? (
                                        <Dropdown.Item
                                            onClick={() => openSettingsModal(cell.row.original.id)}
                                        >
                                            <Icon name="mdi-settings" className="mr-2" />  {i18n.t('jt_settings.text')}
                                        </Dropdown.Item>
                                    ) : (
                                        <Tooltip
                                            content={i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')}
                                            placement="top"
                                        >
                                            <div
                                                className="ml-auto"
                                                style={{
                                                    cursor: 'not-allowed',
                                                    display: 'flex',
                                                }}
                                            >
                                                <Dropdown.Item
                                                    style={{pointerEvents: 'none'}}
                                                    disabled
                                                    data-cy="notifications-list-btn-1">
                                                    <Icon name="mdi-settings" className="mr-2" /> {i18n.t('jt_settings.text')}
                                                </Dropdown.Item>
                                            </div>
                                        </Tooltip>
                                    )
                                }

                                {
                                    canDeleteNotification ? (
                                        <Dropdown.Item
                                            onClick={() => deleteOne(cell.row.original.id, cell.row.original.title)}
                                        >
                                            <Icon name="mdi-delete-outline" className="mr-2" /> {i18n.t('jt_delete.text')}
                                        </Dropdown.Item>
                                    ) : (
                                        <Tooltip
                                            content={i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')}
                                            placement="top"
                                        >
                                            <Dropdown.Item
                                                disabled
                                                data-cy="notifications-list-btn">
                                                <Icon name="mdi-delete-outline" className="mr-2" /> {i18n.t('jt_delete.text')}
                                            </Dropdown.Item>
                                        </Tooltip>
                                    )
                                }
                            </Dropdown.Menu>
                        </Dropdown>
                    </Tooltip>
                </>
            ),
        },
    ];

    return (
        <>
            <HelpTextBlock helpText={i18n.t('ht_email_reports.text')} />
            <Row>
                <Col lg={3}>
                    <Form
                        className="mb-2 search-box"
                        onSubmit={(e: React.FormEvent) => {
                            e.preventDefault();
                        }}
                    >
                        <Form.Control
                            type="text"
                            placeholder={i18n.t('es_search.text')}
                            onChange={(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => inputChange(event)}
                        />
                    </Form>
                </Col>
                <Col lg={9} className="d-flex justify-content-end">
                    {canCreateNotification ? (
                        <Button
                            variant="info"
                            size="sm"
                            className="mb-2"
                            icon="mdi-plus"
                            onClick={() => setSettingsModalOpen(true)}
                        >
                            {i18n.t('th_add_new_email_report.text')}
                        </Button>
                    ) : (
                        <Tooltip
                            content={i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')}
                            placement="top"
                        >
                            <div style={{cursor: 'not-allowed', display: 'flex', alignItems: 'center'}}>
                                <Button
                                    variant="secondary"
                                    size="xs"
                                    icon="mdi-plus"
                                    disabled
                                    style={{pointerEvents: 'none'}}
                                    data-cy="notifications-list-plus-btn">
                                    {i18n.t('th_add_new_email_report.text')}
                                </Button>
                            </div>
                        </Tooltip>
                    )}
                </Col>
            </Row>

            {
                loading ? (
                    <LoadingWrapper loading={true} loadingText={i18n.t('jt_loading_data_general.text')} minHeight="400px"><div /></LoadingWrapper>
                ) : (
                    <>

                        <div className="card">
                            <div className="card-body">
                                <TableComponent data={notifications} columns={columns} />
                                <PaginationComponent
                                    pages={pages}
                                    currentPage={currentPage}
                                    onPageChange={handlePageChange}
                                />
                            </div>
                        </div>
                        {
                            settingsModalOpen ? (
                                <FormModal
                                    isOpen={settingsModalOpen}
                                    onClose={closeSettingsModal}
                                    title={i18n.t('jt_email_report.text')}
                                >
                                    <NotificationsSettingsForm
                                        notificationId={activeNotificationId || undefined}
                                        onDone={closeSettingsModal}
                                    />
                                </FormModal>
                            ) : null
                        }

                        {
                            configureModalOpen ? (
                                <FormModal
                                    isOpen={configureModalOpen}
                                    onClose={closeConfigureModal}
                                    title={
                                        <div>
                                            <p className="mb-1">{i18n.t('jt_configure_the.text')}</p>
                                            <p className="h6 mb-0">
                                                {activeNotificationId ? notifications.find((s: Notification) => s.id === activeNotificationId)?.title : ''}
                                            </p>
                                        </div>
                                    }
                                >
                                    <NotificationConfigureForm
                                        key={configureFormKey}
                                        notificationId={activeNotificationId!}
                                        onFinish={resetConfigureForm}
                                    />

                                    <h5>{i18n.t('jt_edit_email.text')}</h5>
                                    <NotificationUserConfigureTable
                                        notificationId={activeNotificationId!}
                                    />
                                </FormModal>
                            ) : null
                        }

                        <ModalAlert
                            isOpen={deleteConfirmOpen}
                            onClose={closeDeleteConfirm}
                            variant="error"
                            title={i18n.t('jt_confirm_deletion.text')}
                            message={itemToDelete ? i18n.t('es_are_you_sure_delete_notification.text', {title: itemToDelete.title}) : ''}
                            actions={[
                                {
                                    label: i18n.t('jt_delete.text'),
                                    onClick: confirmDelete,
                                    variant: 'danger',
                                    loading: isDeleting
                                }
                            ]}
                            dismissible={!isDeleting}
                        />

                        <ModalAlert
                            isOpen={manualSendOpen}
                            onClose={closeManualSendModal}
                            variant={manualSendVariant}
                            title={manualSendTitle}
                            message={manualSendBody}
                            actions={
                                manualSendStep === 'confirm'
                                    ? [
                                        {
                                            label: i18n.t('jt_send_report_now.text'),
                                            onClick: confirmManualSend,
                                            variant: 'primary',
                                            loading: manualSendLoading,
                                        },
                                    ]
                                    : []
                            }
                            dismissible={manualSendStep === 'done' || !manualSendLoading}
                        />

                    </>
                )
            }
        </>
    );
});

export default NotificationsListView; 